// ============================================================
// Shared utility functions for Dashboard (replaces SharedService methods)
// ============================================================

/** Replaces Angular `indianCurrency` pipe */
export function toIndianCurrency(value: number | string): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '0';
  return num.toLocaleString('en-IN');
}

/** Replaces SharedService.dataFormat() — converts CSV parsed array to objects */
export function parseCsvToObjects(data: string[][]): Record<string, string>[] {
  if (!data || data.length < 2) return [];
  const headers = data[0] as string[];
  return data.slice(1).map((row) => {
    const obj: Record<string, string> = {};
    headers.forEach((key, idx) => {
      obj[key] = row[idx] ?? '';
    });
    return obj;
  });
}

/** Replaces SharedService.calculateSum() */
export function sumField(array: Record<string, any>[], field: string): number {
  return array.reduce((acc, item) => {
    const val = parseFloat(item[field]);
    return acc + (isNaN(val) ? 0 : val);
  }, 0);
}

/** Safe percentage to 2 decimal places */
export function safePct(
  numerator: number,
  denominator: number,
  decimals = 2
): string {
  if (!denominator || denominator === 0) return '0.00';
  return ((numerator / denominator) * 100).toFixed(decimals);
}

/** Replaces SharedService.getMngTypeOnUserType() — maps userType to geo level string */
export type GeoLevel =
  'state' | 'district' | 'edu_district' | 'block' | number | number[];

export function getMngTypeOnUserType(
  usertype: string,
  usertype1: string
): GeoLevel {
  const ut = String(usertype);
  const ut1 = String(usertype1);

  // Special management-filtered logins (return manage_id or manage_id array)
  if (ut === '15' && ut1 === '3') return [4, 9];
  if (ut === '15' && ut1 === '4') return 9;
  if (ut === '15' && ut1 === '5') return [32, 36];
  if (ut === '15' && ut1 === '6') return 5;
  if (ut === '15' && ut1 === '7') return 7;
  if (ut === '69' && ut1 === '25') return [4, 9];
  if (ut === '69' && ut1 === '5') return 4;
  if (ut === '24' && ['1', '2', '3', '5', '6'].includes(ut1)) return 2;

  // State-level
  if (['5', '15', '13', '44', '40', '68'].includes(ut)) return 'state';
  if (ut === '51' && ut1 === '0') return 'state';
  if (ut === '23' && ut1 === '1') return 'state';
  if (['69', '52'].includes(ut) && ut1 === '0') return 'state';
  if (ut === '43' && ut1 === '2') return 'state';

  // District-level
  if (['19', '9', '24', '43', '3', '37', '23', '36'].includes(ut))
    return 'district';

  // Edu-district
  if (ut === '10') return 'edu_district';

  // Block-level
  if (['6', '2', '39'].includes(ut)) return 'block';

  return 'state';
}

/** Replaces SharedService.CalculateTotalBasedOnFilter() — full filter logic */
export function calculateFilteredData(
  data: any[],
  filters: Record<string, boolean>
): any[] {
  if (!data) return [];
  let result = [...data];

  const schoolTypes: string[] = [];
  const manageIds: string[] = [];
  const cattyIds: string[] = [];
  const genderIds: string[] = [];
  const communityIds: string[] = [];

  // School type mapping
  if (filters.governmentChk) schoolTypes.push('1');
  if (filters.fullyAidedChk) {
    schoolTypes.push('2');
    manageIds.push('10', '13', '19', '15', '21');
  }
  if (filters.partiallyAidedChk) {
    schoolTypes.push('4');
    manageIds.push('14', '18', '16');
  }
  if (filters.stateChk) schoolTypes.push('0');
  if (filters.centralGovtChk) {
    schoolTypes.push('5');
    manageIds.push('11', '12', '28', '30', '33', '34');
  }
  if (filters.unAidedChk) {
    schoolTypes.push('3');
    manageIds.push('22', '23', '24', '25', '29', '35', '26');
  }

  // Government management sub-filters
  if (filters.governmentChk) {
    const govManageIds: string[] = [];
    if (filters.schlDepMangeIdChk) govManageIds.push('1');
    if (filters.corpSchoolChk) govManageIds.push('2');
    if (filters.municipalSchoolChk) govManageIds.push('3');
    if (filters.adiDravidaChk) govManageIds.push('4');
    if (filters.forestDeptSchlChk) govManageIds.push('5');
    if (filters.DAWDSChk) govManageIds.push('6');
    if (filters.kallarDeptChk) govManageIds.push('7');
    if (filters.tribalWelChk) govManageIds.push('9');
    if (filters.borstalSchoolChk) govManageIds.push('8');
    if (filters.KGBVChk) govManageIds.push('32');
    if (filters.NSCBAVChk) govManageIds.push('36');
    if (govManageIds.length > 0) {
      manageIds.push(...govManageIds);
    }
  }

  // Category type mapping
  if (filters.primarySchoolChk) cattyIds.push('2', '1');
  if (filters.middleSchoolChk) cattyIds.push('3');
  if (filters.highSchoolChk) cattyIds.push('4');
  if (filters.highSecSchoolChk) cattyIds.push('5');

  // Gender mapping
  if (filters.male) genderIds.push('1');
  if (filters.female) genderIds.push('2');

  // Community mapping
  if (filters.bcOthers) communityIds.push('1');
  if (filters.bcMuslim) communityIds.push('2');
  if (filters.mbc) communityIds.push('3');
  if (filters.st) communityIds.push('4');
  if (filters.scOthers) communityIds.push('5');
  if (filters.SCArunthathiyar) communityIds.push('6');
  if (filters.oc) communityIds.push('7');
  if (filters.dnc) communityIds.push('8');
  if (filters.socialCategory) communityIds.push('9');

  // Apply filters — only filter if NOT all options in a section are selected
  // (all checked = show everything = no filter needed)
  if (schoolTypes.length > 0)
    result = result.filter((x) =>
      schoolTypes.includes(String(x.school_type_id))
    );
  if (cattyIds.length > 0)
    result = result.filter((x) => cattyIds.includes(String(x.catty_id)));
  if (manageIds.length > 0)
    result = result.filter((x) => manageIds.includes(String(x.manage_id)));
  if (genderIds.length > 0)
    result = result.filter((x) => genderIds.includes(String(x.Gender)));
  if (communityIds.length > 0)
    result = result.filter((x) =>
      communityIds.includes(String(x.community_id))
    );

  // Class filter — maps class checkbox to class_studying_id
  const classIds: string[] = [];
  if (filters.pre_kg) classIds.push('1');
  if (filters.lkg) classIds.push('2');
  if (filters.ukg) classIds.push('3');
  if (filters.class1) classIds.push('4');
  if (filters.class2) classIds.push('5');
  if (filters.class3) classIds.push('6');
  if (filters.class4) classIds.push('7');
  if (filters.class5) classIds.push('8');
  if (filters.class6) classIds.push('9');
  if (filters.class7) classIds.push('10');
  if (filters.class8) classIds.push('11');
  if (filters.class9) classIds.push('12');
  if (filters.class10) classIds.push('13');
  if (filters.class11) classIds.push('14');
  if (filters.class12) classIds.push('15');

  // Only apply class filter if at least one class is checked but not all (all = no filter)
  if (classIds.length > 0 && classIds.length < 15) {
    result = result.filter((x) =>
      classIds.includes(String(x.class_studying_id))
    );
  }

  return result;
}
